<template>
    <b-container fluid>
        <b-row class="no-gutters pt-2">
            <b-col md="12">
                <grid
                    :isAutoWidth="true"
                    :isSearch="false"
                    ref="grid"
                    :rowData="rowData"
                    :columnDefs="columnDefs"
                    class="metadata-grid mb-2"
                    :isAutoHeight="true"
                    rowSelection="single"
                    @selection-changed="handleRowSelect"
                     @on-row-data-updated="initialRowSelect"
                >
                    <responsive-button @click="openAddDiscountModal" variant="primary" :icon="['fas', 'plus']"
                        >Add Discount</responsive-button
                    >
                    <b-button-group class="tsa-methods-wrapper">
                        <b-button
                            :class="{ 'active-filter': discountType === 0 }"
                            variant="default"
                            @click="(e) => onClickFilter(0, e)"
                            >Show Promotion</b-button
                        >
                        <b-button
                            :class="{ 'active-filter': discountType === 1 }"
                            variant="default"
                            @click="(e) => onClickFilter(1, e)"
                            >Show Length Of Stay</b-button
                        >
                        <b-button
                            :class="{ 'active-filter': discountType === 2 }"
                            variant="default"
                            @click="(e) => onClickFilter(2, e)"
                            >Show Last Minute</b-button
                        >
                    </b-button-group>
                </grid>

                <grid
                    v-if="selectedRow && rowData.length"
                    :isSearch="false"
                    ref="property_grid"
                    :rowData="properties"
                    :columnDefs="propertiesColumnDefs"
                    :isAutoHeight="true"
                    class="pt-2"
                >
                    <responsive-dropdown class="filter-dropdown" :items="propertiesFilterOptions" variant="default">
                        <span v-html="filterIcon" />{{ filterTitle }}
                    </responsive-dropdown>
                    <responsive-button @click="bulkAssignToProperty(true)" variant="default" :icon="['fas', 'plus']"
                        >Add "{{ truncatedTitle }}" to All Properties</responsive-button
                    >
                    <responsive-button @click="bulkAssignToProperty(false)" variant="default" :icon="['fas', 'minus']"
                        >Remove "{{ truncatedTitle }}" from All Properties</responsive-button
                    >
                </grid>
            </b-col>
        </b-row>
        <add-edit-discount-modal
            @hide="initialValues = null"
            :modalId="modalId"
            :initialData="initialValues"
            @submit="onAddEditRecord"
            :discountType="discountType"
        />
    </b-container>
</template>

<script>
import api from '../../api';
import { mapGetters } from 'vuex';
import { formatResponseError } from '../../utils/api';
import Grid from '../generic/grid/grid.vue';
import { PROPERTY_MANAGER_DISCOUNT_ENDPOINT } from '../../endpoints';
import responsiveButton from '../generic/responsive-button/responsive-button.vue';
import responsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
import EditDeleteActionCellRenderer from './cell-renderers/edit-delete-action-cell-renderer.vue';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import propertyManagerMixin from './property-manager-mixin';
import addEditDiscountModal from './modals/add-edit-discount-modal.vue';

export default {
    mixins: [propertyManagerMixin],
    components: { Grid, responsiveButton, responsiveDropdown, EditDeleteActionCellRenderer, addEditDiscountModal },
    data() {
        return {
            endpoint: PROPERTY_MANAGER_DISCOUNT_ENDPOINT,
            params: {
                discount_type: 0,
            },
            propertyName: 'Discount',
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        promoColumnDefs() {
            return [
                {
                    field: 'title',
                    headerName: 'Title',
                    autoWidthExclude: true,
                    minWidth: 200,
                },
                {
                    field: 'status',
                    headerName: 'Status',
                },
                {
                    field: 'value_display',
                    headerName: 'Amount',
                },
                {
                    field: 'action',
                    headerName: 'Type',
                },
                {
                    field: 'automatic',
                    headerName: 'Apply',
                    cellRenderer: ({ data }) => (data.automatic ? 'Automatic' : 'Manual'),
                },
                {
                    field: 'code',
                    headerName: 'Code',
                    cellRenderer: ({ data }) => (data.code && !data.automatic ? data.code : '-'),
                },
                {
                    field: 'min_nights',
                    headerName: 'Min Nights',
                    minWidth: 120,

                    cellRenderer: ({ data }) => (data.min_nights > 1 ? data.min_nights : '-'),
                },
                {
                    field: '',
                    headerName: 'Travel Date',
                    cellRenderer: ({ data }) => {
                        const startDate = data.start_date ? this.formatDate(data.start_date) : 'N/A';
                        const endDate = data.end_date ? this.formatDate(data.end_date) : 'N/A';
                        return `${startDate} - ${endDate}`;
                    },
                },
                {
                    field: '',
                    headerName: 'Validity Date',
                    minWidth: 120,

                    cellRenderer: ({ data }) => {
                        const startDate = data.validity_start_date ? this.formatDate(data.validity_start_date) : 'N/A';
                        const endDate = data.validity_end_date ? this.formatDate(data.validity_end_date) : 'N/A';
                        return `${startDate} - ${endDate}`;
                    },
                },
                {
                    field: '',
                    cellRenderer: 'EditDeleteActionCellRenderer',
                    maxWidth: 210,
                    minWidth: 210,
                    cellRendererParams: {
                        onEdit: (data) => {
                            this.openEditDiscountModal(data);
                        },
                        onDelete: async (data) => {
                            try {
                                await api.delete(this.endpoint(data.id));
                                this.fetchRowData().then(() => {
                                    this.initialRowSelect();
                                });
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                this.errorToast({ text: generic || 'Could not delete discount' });
                            }
                        },
                        deleteMessage:
                            'Are you sure you want to delete this discount and remove it from all properties?',
                    },
                },
            ];
        },
        lengthOfStayColumnDefs() {
            return [
                {
                    field: 'title',
                    headerName: 'Title',
                    autoWidthExclude: true,
                },
                {
                    field: 'status',
                    headerName: 'Status',
                },
                {
                    field: '',
                    headerName: 'Amount',
                    cellRenderer: ({ data }) => data.modifiers_values_display.join('<br>'),
                    autoHeight: true,
                },
                {
                    field: '',
                    headerName: 'Min Nights',
                    cellRenderer: ({ data }) => data.modifiers_min_nights_display.join('<br>'),
                    autoHeight: true,
                },
                {
                    field: 'action',
                    headerName: 'Type',
                },
                {
                    field: 'automatic',
                    headerName: 'Apply',
                    cellRenderer: ({ data }) => (data.automatic ? 'Automatic' : 'Manual'),
                },
                {
                    field: '',
                    cellRenderer: 'EditDeleteActionCellRenderer',
                    maxWidth: 210,
                    minWidth: 210,
                    cellRendererParams: {
                        onEdit: (data) => {
                            this.openEditDiscountModal(data);
                        },
                        onDelete: async (data) => {
                            try {
                                await api.delete(this.endpoint(data.id));
                                this.fetchRowData().then(() => {
                                    this.initialRowSelect();
                                });
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                this.errorToast({ text: generic || 'Could not delete discount' });
                            }
                        },
                        deleteMessage:
                            'Are you sure you want to delete this discount and remove it from all properties?',
                    },
                },
            ];
        },
        columnDefs() {
            return this.discountType === 0 ? this.promoColumnDefs : this.lengthOfStayColumnDefs;
        },
        discountType() {
            return this.params.discount_type;
        },
    },
    mounted() {
        this.fetchRowData()
    },
    methods: {
        formatDate(date) {
            return format(parseISO(date), this.userUnicodeDateFormat);
        },
        openAddDiscountModal() {
            this.modalId = 'add-discount-modal';
            this.$nextTick(() => {
                this.$bvModal.show(this.modalId);
            });
        },
        openEditDiscountModal(data) {
            this.modalId = 'edit-discount-modal-' + data.id;
            this.initialValues = data;
            this.$nextTick(() => {
                this.$bvModal.show(this.modalId);
            });
        },
        async onClickFilter(value, e) {
            this.params.discount_type = value;
            e.target.blur();
            await this.fetchRowData();
            this.initialRowSelect();
        },
    },
};
</script>

<style scoped lang="scss">
:deep(.metadata-grid) {
    display: inline;

    .ag-row-hover,
    .ag-row-selected {
        background: rgba(33, 150, 243, 0.1);
        cursor: pointer;
    }
}

.select-wrapper {
    display: flex;
    align-items: baseline;
    gap: 5px;

    span {
        width: 250px;
    }
}
</style>
<style lang="scss">
.tsa-methods-wrapper {
    .active-filter {
        background-color: transparent !important;
        border-color: #ccc !important;
        box-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.3) !important;
    }
}
</style>
